import React, { useEffect, useState } from "react";
import {
  useServiceProductsQuery,
  useAddOnProductsQuery,
} from "../../Store/Apis/servicesApi";
import {
  useCreateCustomerMutation,
  useCustomersQuery,
} from "../../Store/Apis/customerApi";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { DatePicker } from "antd";
import moment from "moment";
import { allCountries } from "country-telephone-data";
import Error from "../../Components/Error/Error";
import Loader from "../../Components/Loader/Loader";
import { useSelector } from "react-redux";
import AddPaymentForm from "../../Components/AddPaymentForm/AddPaymentForm";
import { useTaxPercentageQuery } from "../../Store/Apis/toolsApi";
import { useOrderByCustomerIdQuery } from "../../Store/Apis/bookingApi";

const AddEditCharges = ({ onSubmit, data, paymentData }) => {
  const user = useSelector((state) => state.auth.user);
  const [sortFilter, setSortFilter] = useState({
    productSearch: "",
    customerSearch: "",
    outletID: "",
  });

  // Form fields for the integrated customer form
  const [customerForm, setCustomerForm] = useState({
    salutation: "Mr",
    customerName: "",
    dateOfBirth: "",
    countryCode: "+27",
    phoneNumber: "",
    email: "",
    isCustomerSelected: false,
  });

  const serviceProductsApi = useAddOnProductsQuery(
    {
      keyname: sortFilter.productSearch,
      outletID: sortFilter.outletID,
    },
    { skip: !sortFilter.outletID }
  );

  const customerApi = useCustomersQuery(
    {
      keyword: customerForm.customerName, // Using customerName from form for search
      outletID: sortFilter.outletID,
    },
    { skip: !sortFilter.outletID || !customerForm.customerName }
  );

  const [selectedChildren, setSelectedChildren] = useState([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState([]); // Track currently clicked product
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Track selected customer
  const [filterType, setFilterType] = useState("All"); // Add state to track the filter type
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // Default to today's date
  const [subtotal, setSubtotal] = useState(0); // State to store the subtotal
  const [loading, setLoading] = useState(false); // New loading state after form submition
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Calculate and update subtotal whenever selectedChildren changes
  useEffect(() => {
    const newSubtotal = selectedChildren.reduce(
      (acc, child) => acc + (child.rate || 0) * (child.quantity || 1),
      0
    );
    setSubtotal(newSubtotal); // Update subtotal state
  }, [selectedChildren, setSelectedChildren]);

  console.log("this is the selected children", selectedChildren);
  // Handle selecting a product to view details (only clicked product shown)
  const handleSelectProduct = (product) => {
    setSelectedProduct(product); // Set clicked product for modal
    setIsProductModalOpen(true);
  };

  // Handle selecting/unselecting child records
  const handleSelectChild = (child) => {
    setSelectedChildren((prev) => {
      if (prev.find((item) => item.childId === child.childId)) {
        // Unselect child if it's already selected
        return prev.filter((item) => item.childId !== child.childId);
      }
      // Add child with default quantity of 1
      return [
        ...prev,
        {
          ...child,
          quantity: 1,
          rate: child.rate,
          maxQuantity: child.quantity,
        },
      ];
    });
  };

  // Handler to update the selected date
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // Increment or decrement the quantity for a specific child record
  const handleQuantityChange = (child, type) => {
    console.log(child);
    const maxQuantity = child.maxQuantity;
    setSelectedChildren(
      (prevChildren) =>
        prevChildren
          .map((item) => {
            if (item.childId === child.childId) {
              const currentQty = item.quantity || 1;
              let newQty = currentQty;

              if (type === "increment") {
                // newQty = Math.min(currentQty + 1, maxQuantity); // Ensure newQty doesn't exceed maxQuantity
                newQty = currentQty + 1;
              } else if (type === "decrement") {
                newQty = currentQty - 1; // Decrement quantity without restricting to 1
              }

              return { ...item, quantity: newQty };
            }
            return item;
          })
          .filter((item) => item.quantity > 0) // Remove items with quantity <= 0
    );
  };

  // Handle changing the rate for a specific child record
  const handleRateChange = (child, newRate) => {
    // Update the selectedChildren array with the new rate for the specific child
    setSelectedChildren((prevSelectedChildren) =>
      prevSelectedChildren.map((c) =>
        c.childId === child.childId ? { ...c, rate: newRate } : c
      )
    );
  };

  // Handle customer selection from suggestions
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerForm({
      ...customerForm,
      customerName: customer.customerName,
      phoneNumber: customer.phoneNumber,
      email: customer.email,
      isCustomerSelected: true,
    });
    setShowSuggestions(false);
  };

  // Handle customer form input changes
  const handleCustomerFormChange = (e) => {
    const { name, value } = e.target;
    setCustomerForm({
      ...customerForm,
      [name]: value,
      isCustomerSelected: false, // Reset selected status when form is edited
    });

    if (name === "customerName") {
      setShowSuggestions(value.trim().length > 0);
    }
  };

  // Filter products/services based on filterType state
  const filteredProducts =
    serviceProductsApi.isSuccess &&
    serviceProductsApi.data?.data?.filter((product) => {
      if (filterType === "All") return true;
      if (filterType === "Product") return product.type === 1;
      if (filterType === "Service") return product.type === 2;
      return false;
    });

  const [addCustomer, addCustomerResponse] = useCreateCustomerMutation();

  // Handle adding a new customer
  const handleAddCustomer = (e) => {
    e.preventDefault();

    const body = {
      salutation: customerForm.salutation,
      name: customerForm.customerName,
      dateOfBirth: customerForm.dateOfBirth
        ? customerForm.dateOfBirth.toString()
        : "",
      email: customerForm.email,
      countryCode: customerForm.countryCode.toString(),
      customerRating: 0,
      phone: customerForm.phoneNumber.toString(),
      taxNumber: "",
      locality: "",
      pin: "",
      address: "",
      isActive: 1,
      outletID: sortFilter.outletID,
    };

    addCustomer(body)
      .then((res) => {
        console.log(res);
        if (res?.data?.status === 200) {
          toast.success(res?.data?.message, { duration: 7000 });
          // Set the newly created customer as selected
          setSelectedCustomer({
            customerID: res.data.data.customerID,
            customerName: customerForm.customerName,
            phoneNumber: customerForm.phoneNumber,
            email: customerForm.email,
          });
          setCustomerForm({
            ...customerForm,
            isCustomerSelected: true,
          });
        } else {
          toast.error(res?.data?.message, { duration: 7000 });
        }
      })
      .catch((err) => {
        toast.error("An error occurred. Please try again.", { duration: 7000 });
      });
  };

  const [viewCustomerModal, setViewCustomer] = useState(false);
  const ViewCustomer = () => {
    const { data: customerOrders } = useOrderByCustomerIdQuery(
      selectedCustomer?.customerID,
      { skip: !selectedCustomer }
    );

    // Calculate total amounts from orders with safer number conversion
    const orderStats = React.useMemo(() => {
      if (!customerOrders?.data)
        return { orderAmount: 0, receivedAmount: 0, outstandingAmount: 0 };

      return customerOrders.data.reduce(
        (acc, order) => {
          // Safely convert values to numbers, defaulting to 0 if invalid
          const detailPrice = parseFloat(order.detailPrice || 0) || 0;
          const receivedAmount = parseFloat(order.receivedAmount || 0) || 0;

          return {
            orderAmount: acc.orderAmount + detailPrice,
            receivedAmount: acc.receivedAmount + receivedAmount,
            outstandingAmount:
              acc.outstandingAmount + (detailPrice - receivedAmount),
          };
        },
        { orderAmount: 0, receivedAmount: 0, outstandingAmount: 0 }
      );
    }, [customerOrders?.data]);

    return (
      <Modal
        show={viewCustomerModal}
        onHide={() => setViewCustomer(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          className="bg-primary text-white"
          style={{
            borderTopLeftRadius: "0.5rem",
            borderTopRightRadius: "0.5rem",
          }}
        >
          <Modal.Title style={{ color: "white" }}>Customer History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div className="mb-2">
              <div className="row">
                <div className="col-md-12">
                  <div className="row align-items-center">
                    <div className="col-12 mb-2">
                      <h4 className="text-dark mb-0">
                        {selectedCustomer?.customerName}
                      </h4>
                    </div>
                    <div className="col-auto">
                      <strong className="text-dark" style={{ marginRight: 15 }}>
                        CUSTOMER INFO
                      </strong>
                    </div>
                    <div className="col-auto">
                      <div className="text-xs">
                        <i className="fa fa-phone me-2" />
                        {selectedCustomer?.phoneNumber || "Not Provided"}
                      </div>
                    </div>
                    <div className="col-auto">
                      <div className="text-xs">
                        <i className="fa fa-envelope me-2" />
                        {selectedCustomer?.email || "Not Provided"}
                      </div>
                    </div>
                    {customerForm.dateOfBirth && (
                      <div className="col-auto">
                        <div className="text-xs">
                          <i className="fa fa-birthday-cake me-2" />
                          {moment(customerForm.dateOfBirth).format(
                            "DD MMM YYYY"
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-12 mt-4">
                  <div className="row">
                    <div
                      className="col-md-4 text-dark"
                      style={{ fontSize: 14 }}
                    >
                      <div>
                        Order Amount{" "}
                        <span className="text-nowrap fw-bold fs-5 d-block">
                          R {orderStats.orderAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div
                      className="col-md-4 text-dark"
                      style={{ fontSize: 14 }}
                    >
                      <div>
                        Received Amount{" "}
                        <span className="text-nowrap fw-bold fs-5 d-block">
                          R {orderStats.receivedAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div
                      className="col-md-4 text-dark"
                      style={{ fontSize: 14 }}
                    >
                      <div>
                        Outstanding Amount{" "}
                        <span className="text-nowrap fw-bold fs-5 d-block">
                          R {orderStats.outstandingAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order History Section */}
                {customerOrders?.data && customerOrders.data.length > 0 && (
                  <div className="col-md-12 mt-4">
                    <h6 className="mb-3">Order History</h6>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Order Amount</th>
                            <th>Paid Amount</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerOrders?.data?.map((order) => (
                            <tr key={order.orderID}>
                              <td>
                                {moment(order.orderDate).format("DD MMM YYYY")}
                              </td>
                              <td>
                                R{" "}
                                {(
                                  parseFloat(order.detailPrice || 0) || 0
                                ).toFixed(2)}
                              </td>
                              <td>
                                R{" "}
                                {(
                                  parseFloat(order.receivedAmount || 0) || 0
                                ).toFixed(2)}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    order.statusId === 0
                                      ? "bg-success"
                                      : order.statusId === 5
                                      ? "bg-warning"
                                      : "bg-secondary"
                                  }`}
                                >
                                  {order.statusId === 0
                                    ? "Completed"
                                    : order.statusId === 5
                                    ? "Quotation"
                                    : "Quotation"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  const [paymentFormModal, setPaymentFormModal] = useState(false);
  const taxPercentageApi = useTaxPercentageQuery();

  const handleOrder = (paymentData) => {
    const taxPercentage =
      Number(taxPercentageApi?.data?.data?.masterValue) || 0; // Get the tax value or default to 0
    const taxAmount = (subtotal * taxPercentage) / 100;

    const orderBody = {
      customerID: selectedCustomer.customerID,
      customerName: selectedCustomer.customerName,
      categoryID: user.serviceCategoryId,
      outletId: user.outletID,
      phoneNumber: selectedCustomer.phoneNumber,
      email: selectedCustomer.email,

      serviceDetails: selectedChildren.map((elem) => ({
        parentID: elem.parentId,
        serviceID: elem.childId,
        typeID: elem.typeId,
        serviceQuantity: elem.quantity,
        serviceRate: elem.rate,
        detailPrice: elem.rate * elem.quantity,
      })),

      discount: Number(paymentData.discount),
      discountType: Number(paymentData.discountType),
      receivedAmount: Number(paymentData.paidAmount),
      subTotal: Number(subtotal),
      payAmount: Number(paymentData.paidAmount),
      paymentType: Number(paymentData.paymentType),
      paymentNote: paymentData.notes,
      utrNumber: paymentData.utrNumber,
      statusId: paymentData.createQuotation ? 5 : 0,
      taxAmount: taxAmount,
      taxPercentage: taxPercentage,
      detailPrice:
        Number(subtotal) + Number(taxAmount) - Number(paymentData.discount),
      createdBy: Number(user.id),
      orderDate: selectedDate,
    };

    onSubmit(orderBody);
  };

  useEffect(() => {
    // Populate state from data if it exists
    if (data) {
      setSelectedCustomer({
        customerID: data.customerID,
        customerName: data.customerName,
        phoneNumber: data.customerNumber,
        email: data.email,
      });

      setCustomerForm({
        ...customerForm,
        customerName: data.customerName,
        phoneNumber: data.customerNumber,
        email: data.email,
        isCustomerSelected: true,
      });

      setSelectedDate(
        data.orderDate
          ? data.orderDate.split("T")[0]
          : new Date().toISOString().split("T")[0]
      );
      setSubtotal(data.subTotal || 0);

      // Map service details to selectedChildren (normalize API shapes)
      const serviceItems = data?.serviceTypeAndName || data?.services || [];
      setSelectedChildren(
        serviceItems.map((service) => ({
          parentId:
            service.parentID ?? service.parentId ?? service.serviceId ?? null,
          childId:
            service.childID ?? service.childId ?? service.serviceId ?? null,
          typeId:
            service.serviceID ??
            service.typeID ??
            service.serviceTypeId ??
            null,
          typeName: service.serviceName ?? service.typeName ?? "",
          code: service.productServiceName ?? service.code ?? "",
          quantity: service.quantity ?? service.serviceQuantity ?? 1,
          rate: service.rate ?? service.serviceRate ?? 0,
          maxQuantity: service.quantity ?? service.serviceQuantity ?? 1,
          costPrice: service.total ?? service.detailPrice ?? 0,
        }))
      );
    }
  }, [data]);

  useEffect(() => {
    if (user.role !== "superAdmin") {
      setSortFilter({
        ...sortFilter,
        outletID: user.outletID,
      });
    }
  }, []);

  return (
    <div>
      <div className="row align-items-center justify-content-between mb-2">
        <div className="col">
          <h5 className="fw-500 text-white">
            {!data ? "Add" : "Edit"} Booking
          </h5>
        </div>
      </div>
      <div className="row">
        {/* Left side - Add/Edit Booking Form */}
        <div className="col-lg-6 col-12">
          <div className="card mb-4">
            <div className="card-header p-4">
              <h5 className="mb-3">{!data ? "Add" : "Edit"} Booking</h5>

              {/* Integrated Customer Form */}
              <form onSubmit={handleAddCustomer} className="mb-3">
                <div className="mb-3">
                  <label className="form-label">Salutation</label>
                  <select
                    className="form-control"
                    name="salutation"
                    value={customerForm.salutation}
                    onChange={handleCustomerFormChange}
                  >
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                  </select>
                </div>

                <div className="mb-3 position-relative">
                  <label className="form-label">Customer Name*</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Customer Name"
                    name="customerName"
                    value={customerForm.customerName}
                    onChange={handleCustomerFormChange}
                    required
                  />

                  {/* Customer search results dropdown */}
                  {showSuggestions &&
                    customerApi.isSuccess &&
                    customerApi.data.data.length > 0 && (
                      <ul className="list-group position-absolute w-100 z-index-3">
                        {customerApi.data.data.map((customer) => (
                          <li
                            key={customer.customerID}
                            className="list-group-item customhover2 cursor-pointer"
                            onClick={() => handleCustomerSelect(customer)}
                          >
                            <span className="fw-bold d-block">
                              {customer.customerName}
                            </span>
                            <small
                              style={{ fontSize: "12px" }}
                              className="d-block text-warning"
                            >
                              {customer.phoneNumber}
                            </small>
                            <small
                              style={{ fontSize: "12px" }}
                              className="d-block text-danger"
                            >
                              {customer.email}
                            </small>
                            <small
                              style={{ fontSize: "12px" }}
                              className="d-block text-info"
                            >
                              {customer.address}
                            </small>
                          </li>
                        ))}
                      </ul>
                    )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Date of Birth</label>
                  <DatePicker
                    className="form-control"
                    name="dateOfBirth"
                    value={
                      customerForm.dateOfBirth
                        ? moment(customerForm.dateOfBirth, "YYYY-MM-DD")
                        : null
                    }
                    onChange={(date, dateString) =>
                      handleCustomerFormChange({
                        target: { name: "dateOfBirth", value: dateString },
                      })
                    }
                    format="YYYY-MM-DD"
                  />
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Country Code</label>
                    <select
                      className="form-control"
                      name="countryCode"
                      value={customerForm.countryCode}
                      onChange={handleCustomerFormChange}
                    >
                      <option value="">Select Country Code</option>
                      {allCountries.map((country) => (
                        <option
                          key={country.iso2}
                          value={`+${country.dialCode}`}
                        >
                          {country.name} (+{country.dialCode})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">Phone Number*</label>
                    <input
                      type="tel"
                      className="form-control"
                      placeholder="Phone Number"
                      name="phoneNumber"
                      value={customerForm.phoneNumber}
                      onChange={handleCustomerFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Email Address"
                    name="email"
                    value={customerForm.email}
                    onChange={handleCustomerFormChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Booking Date</label>
                  <DatePicker
                    className="form-control"
                    value={
                      selectedDate ? moment(selectedDate, "YYYY-MM-DD") : null
                    }
                    onChange={(date, dateString) => setSelectedDate(dateString)}
                    format="YYYY-MM-DD"
                  />
                </div>

                {/* Submit button only shown if customer not selected */}
                {!customerForm.isCustomerSelected && (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      !customerForm.customerName || !customerForm.phoneNumber
                    }
                  >
                    Add Customer
                  </button>
                )}

                {/* Selected Customer Info */}
                {selectedCustomer && (
                  <div className="mt-3 p-3 border border-light rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">Selected Customer</h6>
                        <p className="mb-0 text-sm">
                          {selectedCustomer.customerName}
                        </p>
                        <small className="text-muted">
                          {selectedCustomer.phoneNumber}
                        </small>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setViewCustomer(true)}
                      >
                        <i className="fa fa-eye me-1"></i> View Details
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>

            <div className="card-body p-0">
              {/* Selected Products/Services Table */}
              {selectedChildren?.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table className="table mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="text-uppercase text-secondary text-xs opacity-7">
                            Product
                          </th>
                          <th className="text-uppercase text-secondary text-xs opacity-7">
                            Service Type
                          </th>
                          <th className="text-uppercase text-secondary text-xs opacity-7">
                            Rate
                          </th>
                          <th className="text-uppercase text-secondary text-xs opacity-7">
                            QTY
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedChildren.map((child, index) => (
                          <tr key={index}>
                            <td
                              className="text-xs font-weight-bold align-content-center px-4"
                              style={{
                                maxWidth: "140px",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                              title={child.code}
                            >
                              {child.code || "No Code"}
                            </td>
                            <td
                              className="text-xs align-content-center px-4"
                              style={{
                                maxWidth: "140px",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                              title={child.typeName}
                            >
                              {child.typeName || "No Service Type"}
                            </td>
                            <td className="align-content-center px-4">
                              <input
                                type="number"
                                className="form-control text-xs"
                                value={child.rate || ""}
                                onChange={(e) =>
                                  handleRateChange(child, e.target.value)
                                }
                                style={{ width: "80px" }}
                              />
                            </td>
                            <td className="align-content-center px-4">
                              <div className="input-group align-items-center">
                                <button
                                  type="button"
                                  className="btn mb-0 badge bg-primary text-xxs text-center p-66"
                                  onClick={() =>
                                    handleQuantityChange(child, "decrement")
                                  }
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-center"
                                  disabled
                                  value={child.quantity || 1}
                                  style={{ width: "40px", flexGrow: "inherit" }}
                                />
                                <button
                                  type="button"
                                  className="btn mb-0 badge bg-primary text-xxs text-center p-66"
                                  onClick={() =>
                                    handleQuantityChange(child, "increment")
                                  }
                                >
                                  +
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <hr />
                  <div className="row align-items-center px-4 mb-3">
                    <div className="col">
                      <p className="text-sm mb-0 fw-500">Sub Total</p>
                      <p className="text-sm text-success fw-600 mb-0">
                        Subtotal: R {subtotal}
                      </p>
                    </div>
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-danger bg-danger me-2 mb-0 btn-sm"
                        disabled={selectedChildren?.length <= 0}
                        onClick={() => (
                          setSelectedChildren([]),
                          setSelectedCustomer(null),
                          setCustomerForm({
                            ...customerForm,
                            isCustomerSelected: false,
                          })
                        )}
                      >
                        Clear All
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary mb-0 btn-sm"
                        disabled={
                          !selectedCustomer ||
                          selectedChildren?.length <= 0 ||
                          loading
                        }
                        onClick={() => setPaymentFormModal(true)}
                      >
                        {loading ? "Processing..." : "Save and Continue"}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h6 className="px-4 text-center my-4 py-5">
                    No Product/ Services Selected Yet.
                  </h6>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Product/Service Selection */}
        <div className="col-lg-6 col-12">
          <div className="card mb-4">
            <div className="card-header p-4">
              <h5 className="mb-3">Select Product/ Services</h5>
              <div className="mb-3">
                {/* Filter buttons */}
                <button
                  className={`btn btn-sm mb-0 me-1 ${
                    filterType === "All" ? "btn-primary" : "active"
                  }`}
                  type="button"
                  onClick={() => setFilterType("All")}
                >
                  All
                </button>
                <button
                  className={`btn btn-sm mb-0 me-1 ${
                    filterType === "Product" ? "btn-primary" : "active"
                  }`}
                  type="button"
                  onClick={() => setFilterType("Product")}
                >
                  Product
                </button>
                <button
                  className={`btn btn-sm mb-0 me-1 ${
                    filterType === "Service" ? "btn-primary" : "active"
                  }`}
                  type="button"
                  onClick={() => setFilterType("Service")}
                >
                  Service
                </button>
              </div>
              <div className="col-md-12">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Product Name"
                  value={sortFilter.productSearch}
                  onChange={(e) =>
                    setSortFilter({
                      ...sortFilter,
                      productSearch: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="pos-card-wrapper-scroll-y my-custom-scrollbar-pos-card mb-3">
              {serviceProductsApi.isError && <Error />}
              {serviceProductsApi.isLoading && <Loader height="300px" />}
              {serviceProductsApi.isSuccess && (
                <div className="row g-2 px-4">
                  <>
                    {filteredProducts?.length > 0 ? (
                      filteredProducts.map((elem, i) => {
                        return (
                          <div className="col-md-3 col-sm-4 col-6" key={i}>
                            <div
                              className={`${
                                elem.childRecords.some((child) =>
                                  selectedChildren.some(
                                    (selected) =>
                                      selected.childId.toString() ===
                                      child.childId.toString()
                                  )
                                )
                                  ? "selected_item"
                                  : ""
                              } 
                                                                border-dashed border-1 border-secondary border-radius-md py-2 position-relative p-2 h-100 cursor-pointer`}
                              onClick={() => handleSelectProduct(elem)}
                            >
                              {elem.type === 1 ? (
                                <span
                                  className="badge badge-primary mb-2"
                                  style={{ fontSize: "10px" }}
                                >
                                  Product
                                </span>
                              ) : (
                                <span
                                  className="badge badge-dark mb-2"
                                  style={{ fontSize: "10px" }}
                                >
                                  Service
                                </span>
                              )}
                              {elem.iconImage && (
                                <img
                                  src={elem.iconImage}
                                  alt=""
                                  height={40}
                                  className="mb-2 d-block"
                                />
                              )}
                              <p className="text-xs font-weight-bold mb-0 d-block text-dark">
                                {elem.productServiceName}
                              </p>
                              <small
                                style={{
                                  fontSize: "12px",
                                  marginTop: "0px",
                                  display: "block",
                                }}
                              >
                                {elem.brandName.toLowerCase()}
                              </small>
                              <small
                                style={{
                                  fontSize: "12px",
                                  marginTop: "0px",
                                  display: "block",
                                }}
                                className="text-orange fw-bold mt-1"
                              >
                                {elem.childRecords?.length}{" "}
                                {elem.childRecords?.length > 1
                                  ? "items"
                                  : "item"}
                              </small>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <h6 className="text-center">
                        No Products/ Services Found !!
                      </h6>
                    )}
                  </>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Product Details Modal */}
      {selectedProduct && (
        <Modal
          show={isProductModalOpen}
          onHide={() => setIsProductModalOpen(false)}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedProduct?.productServiceName || "Product Details"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="text-uppercase text-secondary text-xs opacity-7">
                      Service Type
                    </th>
                    <th className="text-uppercase text-secondary text-xs opacity-7">
                      Quantity
                    </th>
                    <th className="text-uppercase text-secondary text-xs opacity-7">
                      Rate
                    </th>
                    <th className="text-uppercase text-secondary text-xs opacity-7">
                      Select
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProduct?.childRecords?.map((child, index) => {
                    const isSelected = selectedChildren.some(
                      (item) => item.childId === child.childId
                    );
                    return (
                      <tr key={index}>
                        <td
                          className="text-xs align-content-center px-4"
                          style={{
                            maxWidth: "140px",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                          title={child.typeName}
                        >
                          {child.typeName || "Not specified"}
                        </td>
                        <td className="text-xs align-content-center px-4">
                          {child.quantity || "N/A"}
                        </td>
                        <td className="text-xs align-content-center px-4">
                          R {child.rate || "0"}
                        </td>
                        <td className="text-xs align-content-center px-4">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              style={{
                                width: "25px",
                                height: "25px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              className="form-check-input"
                              checked={isSelected}
                              onChange={() =>
                                handleSelectChild({
                                  ...child,
                                  parentId: selectedProduct.parentId,
                                })
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setIsProductModalOpen(false)}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* View Customer Modal */}
      <ViewCustomer />

      {/* Payment Form Modal */}
      <Modal
        show={paymentFormModal}
        onHide={() => setPaymentFormModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Payment Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddPaymentForm
            handleSubmit={handleOrder} // Correctly passed handleSubmit
            totalAmount={subtotal} // Passes the subtotal for calculations
            setPaymentFormModal={setPaymentFormModal} // Ensures the Cancel button can close the modal
            data={paymentData} // Passes any initial payment data
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AddEditCharges;
